import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Logo } from "../components/Logo";
import PixelArtBox from "../components/PixelArtBox";
import { ThemeToggle } from "../components/ThemeToggle";
import { AmbientBlobs } from "../components/motion/AmbientBlobs";
import { Stagger } from "../components/motion/Stagger";
import { NumberTicker } from "../components/motion/NumberTicker";
import { StatsTicker } from "../components/StatsTicker";
import { TopEarnersTicker } from "../components/TopEarnersTicker";
import { RecentCreativesCarousel } from "../components/RecentCreativesCarousel";
import { useAbVariant } from "../hooks/useAbVariant";
import {
  Sparkles,
  Instagram,
  ShieldCheck,
  Zap,
  Calendar,
  BarChart3,
  ArrowRight,
  ArrowUpRight,
  Check,
  Play,
  Video,
  Wand2,
  TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [prices, setPrices] = useState({
    starter: 9.99, premium: 19.99, pro: 29.99, platinum: 39.99,
  });
  // A/B experiments — each assignment is independent so we can isolate the
  // effect of each change. The same `cta_click` event fires for all three
  // when any hero/pricing CTA is pressed, so we can compare conversion per
  // variant per experiment cleanly.
  const { track: trackTicker } =
    useAbVariant("stats_ticker_copy", ["control", "urgency", "proof"]);
  const { variant: headlineVariant, track: trackHeadline } =
    useAbVariant("hero_headline", ["control", "outcome", "fomo"]);
  const { variant: ctaCopyVariant, track: trackCtaCopy } =
    useAbVariant("hero_cta_copy", ["control", "action", "value"]);
  const { variant: pricingVariant, track: trackPricing } =
    useAbVariant("pricing_featured", ["premium", "pro", "platinum"]);

  // Centralized click handler — fires `cta_click` against every active
  // experiment so each one builds its own conversion sample independently.
  const onCtaClick = () => {
    trackTicker("cta_click");
    trackHeadline("cta_click");
    trackCtaCopy("cta_click");
    trackPricing("cta_click");
  };

  // ── Hero headline copy variants ────────────────────────────────────
  const HEADLINES = {
    control: { line1: "Imagine it.", emph: "XEA renders it.", line3: "Images, video & 3D — one studio." },
    outcome: { line1: "Stop describing ideas.", emph: "Start shipping them.", line3: "AI image & video, movie-grade." },
    fomo:    { line1: "Creators everywhere ship", emph: "cinema-grade content.", line3: "You should too." },
  };
  const headline = HEADLINES[headlineVariant] || HEADLINES.control;

  // ── Hero primary CTA copy ──────────────────────────────────────────
  const CTA_LABELS = {
    control: "Start free",
    action:  "Create my first render",
    value:   "Get 25 free tokens",
  };
  const ctaLabel = CTA_LABELS[ctaCopyVariant] || CTA_LABELS.control;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/subscriptions/plans`);
        const map = {};
        (r.data.plans || []).forEach((p) => (map[p.name.toLowerCase()] = p.price));
        setPrices({
          starter:  map.starter  || 9.99,
          premium:  map.premium  || 19.99,
          pro:      map.pro      || 29.99,
          platinum: map.platinum || 39.99,
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const features = [
    { icon: Wand2, title: "Design-grade images in seconds", body: "Describe it — logos, posters, product shots, illustrations. Photoreal or stylized, any aspect ratio." },
    { icon: Video, title: "Native-audio video, on demand", body: "Kling-rendered clips and full short films with voices, music & effects. No editing software required." },
    { icon: TrendingUp, title: "3D studio — coming soon", body: "Scan real objects or design your own, then feed them straight to a 3D printer. The next XEA realm." },
    { icon: Instagram, title: "Built for Reels, Shorts, TikTok", body: "Every render is sized and export-ready for the platforms where your work gets seen." },
    { icon: ShieldCheck, title: "Child-safe by default", body: "Adult content is blocked at the platform level. XEA is a family-safe creative ecosystem." },
    { icon: Zap, title: "Tokens, not quotas", body: "One pool. Spend it on videos, images, films, effects — whatever you are creating this week." },
  ];

  // A/B-driven "featured" tier — overrides the hardcoded featured flags
  // based on the assigned variant of the `pricing_featured` experiment.
  // We test which highlighted tier converts best (premium = cheaper anchor,
  // pro = balanced sweet spot, platinum = premium positioning).
  const featuredTier = pricingVariant; // already constrained to one of these
  const plans = [
    {
      name: "Starter",
      price: `$${prices.starter}`,
      tokens: 100,
      features: [
        "100 tokens / month",
        "~10 videos or 20 images",
        "AI text (GPT-5.2)",
        "Instagram & TikTok ready",
      ],
      cta: "Start with Starter",
      featured: false,
    },
    {
      name: "Premium",
      price: `$${prices.premium}`,
      tokens: 200,
      features: [
        "200 tokens / month",
        "~20 videos or 40 images",
        "AI text (GPT-5.2)",
        "Priority support",
      ],
      cta: "Go Premium",
      featured: featuredTier === "premium",
    },
    {
      name: "Pro",
      price: `$${prices.pro}`,
      tokens: 300,
      features: [
        "300 tokens / month",
        "~30 videos or 60 images",
        "Gemini 3 Flash + Nano Banana",
        "AI image enhancement",
        "Auto Instagram Stories",
      ],
      cta: "Go Pro",
      featured: featuredTier === "pro",
    },
    {
      name: "Platinum",
      price: `$${prices.platinum}`,
      tokens: 400,
      features: [
        "400 tokens / month",
        "~40 videos or 80 images",
        "Gemini 3 Flash + Nano Banana",
        "30 Instagram + 30 TikTok / mo",
        "24/7 priority support",
      ],
      cta: "Go Platinum",
      featured: featuredTier === "platinum",
    },
  ];

  const marqueeItems = [
    "Prompt-to-image in the Studio",
    "AI short films with voices & music",
    "12,481 renders created",
    "Native-audio videos with Kling v2.6",
    "Family-safe by design",
    "3D design & print — coming soon",
  ];

  return (
    <div className="min-h-screen bg-ink-50 text-ink-950 overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-4 left-0 right-0 z-50 px-4">
        <div className="max-w-6xl mx-auto rounded-full border border-ink-200/70 bg-white/80 backdrop-blur-2xl shadow-ambient px-4 py-2.5 flex items-center justify-between">
          <Logo size={30} tagline={false} animated />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="nav-login-btn">Login</Button>
            </Link>
            <Link to="/register" onClick={onCtaClick}>
              <Button variant="default" size="sm" data-testid="nav-register-btn">
                Get started <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-40 pb-28 px-6">
        <AmbientBlobs />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7 space-y-8">
              <Badge variant="accent" data-testid="hero-eyebrow">
                <Sparkles className="w-3 h-3" /> AI image &amp; video ecosystem
              </Badge>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-[-0.04em] leading-[0.95]"
                data-testid="hero-headline"
                data-ab-headline={headlineVariant}
              >
                {headline.line1}
                <br />
                <span className="relative inline-block">
                  {headline.emph}
                  <span className="absolute -bottom-2 left-0 right-0 h-3 -z-10 bg-citrus-500/80 rounded-sm" />
                </span>
                <br />
                {headline.line3}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="text-lg sm:text-xl text-ink-500 max-w-xl leading-relaxed font-light"
              >
                One account, one creative ecosystem. Design images in the Studio, direct AI-rendered films and CGI effects — and soon, scan &amp; design real objects for 3D printing.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.25 }}
                className="flex flex-wrap gap-3"
              >
                <Link to="/register" onClick={onCtaClick}>
                  <Button
                    size="lg"
                    data-testid="hero-get-started-btn"
                    data-ab-cta-copy={ctaCopyVariant}
                  >
                    {ctaLabel} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" data-testid="hero-demo-btn">
                  <Play className="w-4 h-4" /> Watch a 30s demo
                </Button>
              </motion.div>

              <div className="flex items-center gap-6 pt-2">
                <div className="flex -space-x-3">
                  {["A", "M", "P", "T"].map((c, i) => (
                    <div
                      key={i}
                      className="w-9 h-9 rounded-full border-2 border-ink-50 bg-ink-950 text-white flex items-center justify-center text-xs font-bold"
                    >
                      {c}
                    </div>
                  ))}
                </div>
                <div className="text-sm leading-tight">
                  <p className="font-bold text-ink-950">
                    <NumberTicker value={12481} testid="hero-creatives-count" /> renders created
                  </p>
                  <p className="text-ink-500 text-xs">images, films & CGI effects by XEA creators</p>
                </div>
              </div>
            </div>

            {/* Floating preview card */}
            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-3xl border border-ink-200/70 bg-white shadow-elevated overflow-hidden"
              >
                <div className="aspect-[4/5] bg-gradient-to-br from-violet-500/10 via-blue-500/10 to-white relative flex items-center justify-center p-6">
                  <div className="w-full h-full" data-testid="hero-pixel-art">
                    <PixelArtBox size={380} />
                  </div>
                  <div className="absolute top-4 left-4 right-4 flex justify-between pointer-events-none">
                    <Badge variant="default">Live preview</Badge>
                    <Badge variant="accent">Spark • always on</Badge>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Live social-proof — animated counters above the marquee */}
      <StatsTicker />

      {/* Top earners this month — public proof that creators are earning XEA */}
      <TopEarnersTicker />

      {/* Live moodboard — sanitized carousel of the latest 12 creatives */}
      <RecentCreativesCarousel />

      {/* Marquee */}
      <section className="overflow-hidden border-y border-ink-200 py-6 bg-white">
        <div className="marquee text-xs font-bold uppercase tracking-[0.22em] text-ink-500">
          {[...marqueeItems, ...marqueeItems].map((m, i) => (
            <span key={i} className="inline-flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-coral-500" />
              {m}
            </span>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-6 mb-10">
            <div className="lg:col-span-6">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-coral-500">What it does</p>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-[-0.03em] mt-4 leading-[1]">
                One agent. <br /> Every creative your store needs.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-ink-500 font-light text-base leading-relaxed">
                XEA pairs GPT-Image & Nano Banana for imagery with Kling v2.6 for native-audio video — with a 3D design & print studio on the way.
              </p>
            </div>
          </div>

          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <Stagger.Item key={i}>
                <div className="group h-full p-7 rounded-3xl border border-ink-200/70 bg-white hover:border-ink-950 transition-all duration-300 hover:-translate-y-1 hover:shadow-elevated">
                  <div className="w-11 h-11 rounded-2xl bg-ink-950 text-white flex items-center justify-center mb-5 group-hover:bg-coral-500 transition-colors">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display font-bold text-xl tracking-tight leading-snug">{f.title}</h3>
                  <p className="text-sm text-ink-500 mt-2 leading-relaxed font-light">{f.body}</p>
                </div>
              </Stagger.Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Mission */}
      <section className="px-6 py-20">
        <div className="max-w-5xl mx-auto rounded-3xl bg-ink-950 text-white p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-coral-500/30 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-2">
              <ShieldCheck className="w-12 h-12 text-citrus-500" />
            </div>
            <div className="lg:col-span-10">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-citrus-500">Our mission</p>
              <h3 className="font-display font-black text-3xl sm:text-4xl tracking-[-0.03em] mt-3 leading-tight">
                Protect children. Always.
              </h3>
              <p className="mt-4 text-white/70 text-base font-light leading-relaxed max-w-2xl">
                Adult content is permanently blocked on XEA. Any attempt to market inappropriate products results in immediate termination — no exceptions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-coral-500">Pricing</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-[-0.03em] mt-3 leading-tight">
              Simple tokens. Real output.
            </h2>
            <p className="text-ink-500 max-w-lg mx-auto mt-4 font-light">
              No per-feature gotchas. Pick a tier, spend tokens on whatever your campaign needs.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`relative p-7 rounded-3xl border transition-all
                  ${plan.featured
                    ? "bg-ink-950 text-white border-ink-950 shadow-coral"
                    : "bg-white border-ink-200/70"}
                `}
              >
                {plan.featured && (
                  <Badge variant="accent" className="absolute -top-3 left-8">
                    Most popular
                  </Badge>
                )}
                <h3 className="font-display font-bold text-2xl tracking-tight">{plan.name}</h3>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display font-black text-5xl tracking-[-0.04em]">{plan.price}</span>
                  <span className={`text-sm font-light ${plan.featured ? "text-white/60" : "text-ink-500"}`}>/mo</span>
                </div>
                <p className={`mt-2 text-sm font-light ${plan.featured ? "text-white/70" : "text-ink-500"}`}>
                  {plan.tokens} tokens monthly
                </p>

                <ul className="space-y-3 my-7">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check className={`w-4 h-4 mt-1 ${plan.featured ? "text-citrus-500" : "text-coral-500"}`} />
                      <span className={`text-sm font-light ${plan.featured ? "text-white/85" : "text-ink-700"}`}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/register" onClick={onCtaClick}>
                  <Button
                    variant={plan.featured ? "accent" : "default"}
                    size="lg"
                    className="w-full"
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  >
                    {plan.cta} <ArrowUpRight className="w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto rounded-3xl border border-ink-200/70 bg-white p-12 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-coral-500/15 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-citrus-500/30 blur-3xl pointer-events-none" />
          <div className="relative">
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-[-0.03em] leading-tight">
              Let XEA handle the content. <br /> You handle the customers.
            </h2>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/register" onClick={onCtaClick}>
                <Button size="lg" data-testid="cta-start-btn">Start free today <ArrowRight className="w-4 h-4" /></Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline">I have an account</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-ink-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo size={30} tagline animated />
          <div className="flex items-center gap-4 text-xs text-ink-500">
            <Link to="/privacy" className="hover:text-ink-950">Privacy</Link>
            <span className="w-1 h-1 rounded-full bg-ink-300" />
            <Link to="/terms" className="hover:text-ink-950">Terms</Link>
            <span className="w-1 h-1 rounded-full bg-ink-300" />
            <Link to="/cookies" className="hover:text-ink-950">Cookies</Link>
          </div>
          <p className="text-xs text-ink-500">© 2026 XEA. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
